import { print as gqlToString } from "graphql/language/printer";

import {
  DeleteCustomSizePayload,
  DeleteCustomSizeResponse,
  GQLResponse,
  ProductAttributeValue,
} from "@sourceful/shared-types";
import { CUSTOM_ATTRIBUTE_VALUES_BY_ATTRIBUTE_ID } from "@gql/mutations/product/customAttributeValuesByAttributeId";
import {
  AddCustomAttributeValueManyMutation,
  AddCustomAttributeValueManyMutationVariables,
  CustomAttributeValueFieldsFragment,
  CustomAttributeValuesByAttributeIdQuery,
  CustomAttributeValuesByAttributeIdQueryVariables,
  CustomAttributeValuesByAttributeIdsQuery,
  CustomAttributeValuesByAttributeIdsQueryVariables,
} from "@generated/graphql";
import { ADD_CUSTOM_ATTRIBUTE_VALUE_MANY } from "@gql/mutations/product/insertCusttomAttributeValueMany";
import { storageAvailable } from "@lib/utils/storageAvailable";
import { CUSTOM_ATTRIBUTE_VALUES_BY_ATTRIBUTE_IDS } from "@gql/mutations/product/customAttributeValuesByAttributeIds";
import getAxiosClient from "@lib/getAxiosClient";
import { apiConfig } from "../../../apiConfig";
import { getOperationNameFromRawQuery, graphQLRequest } from "@lib/hooks/useGraphQLRequest";
import { ProductCloudRole } from "@sourceful/shared-utils/rbac";
import { CLAIMS_DOMAIN, useAuthentication } from "@providers/AuthenticationProvider";
import { useEffect, useState } from "react";

export interface CustomValuesByAttrId {
  [attrId: string]: ProductAttributeValue[];
}

interface ConstructorArgs {
  role: ProductCloudRole | null;
  localStorageKey?: string;
  token: string | null;
  userId: string | null;
  userUuidInternal: string | null;
}

export const CUSTOM_VALUES_LOCAL_STORAGE_KEY = "configuratorCustomValues";

export type DeleteCustomValueArgs = { sizeAttributeSanityId: string } & DeleteCustomSizePayload;

function formatRawDbValue(item: CustomAttributeValueFieldsFragment) {
  return {
    id: item.id,
    label: item?.label || "Custom Value",
    isCustom: true,
    value: item.value,
  };
}

export const isUniqueKeyViolationError = (error: any) => {
  return (
    error instanceof Error &&
    error.message.includes("Uniqueness violation. duplicate key value violates unique constraint")
  );
};

export const useCustomValueManager = () => {
  const { hasuraRole, user, getAccessTokenSilently } = useAuthentication();
  const [manager, setManager] = useState<CustomValueManager>(
    new CustomValueManager({
      userUuidInternal: null,
      userId: null,
      token: null,
      role: null,
    })
  );

  useEffect(() => {
    if (user?.sub) {
      const setup = async () => {
        setManager(
          new CustomValueManager({
            userId: user?.sub || "",
            userUuidInternal:  user  ? user[CLAIMS_DOMAIN].user.uuid : "",
            token: await getAccessTokenSilently(),
            role: hasuraRole || null,
          })
        );
      };

      setup();
    }
  }, [user?.sub, getAccessTokenSilently, hasuraRole]);

  return { customValueManager: manager };
};
export class CustomValueManager {
  private localStorageKey: string = CUSTOM_VALUES_LOCAL_STORAGE_KEY;
  private token?: string;
  private role?: ProductCloudRole;
  private userId: string;
  private userUuidInternal: string;
  private localStorageAvailable: boolean;

  get userToken() {
    return this.token;
  }

  constructor({ localStorageKey, token, userId, userUuidInternal, role }: ConstructorArgs) {
    this.localStorageAvailable = storageAvailable("localStorage");

    if (localStorageKey) {
      this.localStorageKey = localStorageKey;
    }

    this.userId = userId || "";
    this.userUuidInternal = userUuidInternal || "";

    if (token) {
      this.token = token;
    }
    if (role) {
      this.role = role;
    }
  }

  private async fetchFromDbByAttributeId(attributeId: string): Promise<ProductAttributeValue[]> {
    const rawQuery = gqlToString(CUSTOM_ATTRIBUTE_VALUES_BY_ATTRIBUTE_ID);
    const operationName = getOperationNameFromRawQuery(rawQuery);

    const response = await graphQLRequest<
      CustomAttributeValuesByAttributeIdQueryVariables,
      GQLResponse<CustomAttributeValuesByAttributeIdQuery>
    >(rawQuery, {
      variables: {
        attribute_id: attributeId,
        user_id: this.userId,
        // user_uuid_internal: this.userUuidInternal,
      },
      token: this.token,
      operationName,
      role: this.role,
    });

    const rawData = response?.data?.custom_attribute_value || [];
    const formattedValues: ProductAttributeValue[] = rawData.map(formatRawDbValue);
    return formattedValues;
  }

  private async fetchFromDbByAttributeIds(sanityIds: string[]): Promise<CustomValuesByAttrId> {
    const rawQuery = gqlToString(CUSTOM_ATTRIBUTE_VALUES_BY_ATTRIBUTE_IDS);
    const operationName = getOperationNameFromRawQuery(rawQuery);

    const response = await graphQLRequest<
      CustomAttributeValuesByAttributeIdsQueryVariables,
      GQLResponse<CustomAttributeValuesByAttributeIdsQuery>
    >(rawQuery, {
      variables: {
        attribute_ids: sanityIds,
        user_id: this.userId,
      },
      token: this.token,
      operationName,
      role: this.role,
    });

    const rawData = response?.data?.custom_attribute_value || [];

    return rawData.reduce((acc, value) => {
      if (!acc[value.attribute_id]) {
        acc[value.attribute_id] = [];
      }

      acc[value.attribute_id].push(formatRawDbValue(value));
      return acc;
    }, {} as CustomValuesByAttrId);
  }

  private async addCustomValuesToDb(
    attributeSanityId: string,
    newValues: ProductAttributeValue[]
  ): Promise<ProductAttributeValue[]> {
    const rawQuery = gqlToString(ADD_CUSTOM_ATTRIBUTE_VALUE_MANY);
    const operationName = getOperationNameFromRawQuery(rawQuery);

    const response = await graphQLRequest<
      AddCustomAttributeValueManyMutationVariables,
      GQLResponse<AddCustomAttributeValueManyMutation>
    >(rawQuery, {
      variables: {
        objects: newValues.map(newValue => ({
          id: newValue.id,
          attribute_id: attributeSanityId,
          label: newValue.label,
          value: newValue.value,
          created_by_user_uid: this.userId, // TODO: pass in org_id once orgs are sorted out
          created_by_user_uuid_internal: this.userUuidInternal, // TODO: pass in org_id once orgs are sorted out
        })),
      },
      token: this.token,
      operationName,
      role: this.role,
    });

    const rawData = response?.data?.insert_custom_attribute_value?.returning;
    if (rawData) {
      return rawData.map(formatRawDbValue);
    } else {
      throw new Error(`Failed to create value`);
    }
  }

  getAllLocalValues(): CustomValuesByAttrId {
    const customValuesRaw = localStorage?.getItem(CUSTOM_VALUES_LOCAL_STORAGE_KEY) || "{}";
    return JSON.parse(customValuesRaw);
  }

  hasLocalValues(): boolean {
    const values = this.getAllLocalValues();
    return Object.values(values).some(customValArr => customValArr.length > 0);
  }

  private clearLocalValuesForAttribute(attributeId: string) {
    const values = this.getAllLocalValues();

    const updatedValues = {
      ...values,
      [attributeId]: [],
    };

    this.localStorageAvailable &&
      localStorage.setItem(this.localStorageKey, JSON.stringify(updatedValues));
  }

  private async saveLocalValuesToDbOneByOne(attributeId: string, values: ProductAttributeValue[]) {
    for (let value of values) {
      try {
        await this.addCustomValuesToDb(attributeId, [value]);
        this.removeValueFromLocalStorage(attributeId, value.id);
      } catch (error) {
        if (isUniqueKeyViolationError(error)) {
          console.log("Duplicate value found, clearing local values for attribute");
          this.removeValueFromLocalStorage(attributeId, value.id);
        } else {
          throw error;
        }
      }
    }
  }

  async saveLocalValuesToDb() {
    if (!(this.hasLocalValues() && this.userId)) {
      return;
    }

    const localValues = this.getAllLocalValues();

    for (let attributeId in localValues) {
      const values = localValues[attributeId];
      try {
        // attempt to insert multiple values for a given attribute id at once
        await this.addCustomValuesToDb(attributeId, values);
        this.clearLocalValuesForAttribute(attributeId);
      } catch (error) {
        // if there is a unique key violation, try to insert values one by one, ignoring the value that caused the error
        if (isUniqueKeyViolationError(error)) {
          await this.saveLocalValuesToDbOneByOne(attributeId, values);
        } else {
          throw error;
        }
      }
    }
  }

  async getByAttributeIds(attributeSanityIds: string[]): Promise<CustomValuesByAttrId> {
    const localValues = this.getAllLocalValues();

    if (!this.token) {
      return attributeSanityIds.reduce((acc, sanityId) => {
        acc[sanityId] = localValues[sanityId] || [];
        return acc;
      }, {} as CustomValuesByAttrId);
    }

    const userValues = (await this.fetchFromDbByAttributeIds(attributeSanityIds)) || {};
    return userValues;
  }

  async getByAttributeId(attributeId: string): Promise<ProductAttributeValue[]> {
    const localValues = this.getAllLocalValues();
    const localValuesForAttribute = localValues[attributeId] || [];

    if (this.token) {
      const dbValues = await this.fetchFromDbByAttributeId(attributeId);
      return dbValues;
    }

    return localValuesForAttribute;
  }

  async addValueForAttribute(
    attributeSanityId: string,
    newValue: ProductAttributeValue
  ): Promise<ProductAttributeValue> {
    if (this.token) {
      const [value] = await this.addCustomValuesToDb(attributeSanityId, [newValue]);
      return value;
    } else {
      // local storage
      const values = this.getAllLocalValues();

      const updatedValues = {
        ...values,
        [attributeSanityId]: [...(values[attributeSanityId] || []), newValue],
      };
      this.localStorageAvailable &&
        localStorage?.setItem(this.localStorageKey, JSON.stringify(updatedValues));
      return newValue;
    }
  }

  private removeValueFromLocalStorage(attributeId: string, valueId: string) {
    const values = this.getAllLocalValues();

    const updatedValues = {
      ...values,
      [attributeId]: (values[attributeId] || []).filter(val => val.id !== valueId),
    };

    this.localStorageAvailable &&
      localStorage?.setItem(this.localStorageKey, JSON.stringify(updatedValues));

    return [valueId];
  }

  async removeValueForAttribute({
    sizeAttributeSanityId,
    ...payload
  }: DeleteCustomValueArgs): Promise<{ success: boolean; errorMessage?: string }> {
    if (this.token) {
      try {
        const axios = getAxiosClient(this.token, {
          // Promise only resolves if its in the 200 we need to tweak that
          // Got all the bases covered below
          validateStatus: status => {
            return status < 500;
          },
        });

        const { data } = await axios.post<DeleteCustomSizeResponse>(
          `${apiConfig.productCloudApiOrigin}/configuration-item/delete-custom-size`,
          payload
        );

        if (data.success) {
          return {
            success: true,
          };
        }

        return {
          success: false,
          errorMessage: "Cannot delete this custom size as it is used in another draft/order",
        };
      } catch (error) {
        return {
          success: false,
          errorMessage: "Unknown error attempting to delete custom size",
        };
      }
    } else {
      const removedValue = this.removeValueFromLocalStorage(
        sizeAttributeSanityId,
        payload.customValueRecordPk
      );

      return { success: removedValue !== null };
    }
  }
}
